import { useCallback, useEffect, useState } from 'react'
import { Printer as PrinterIcon, X, Loader2 } from 'lucide-react'
import { printersService } from '../../../services/printersService'
import { useSelectedPropertyId } from '../../../store/propertyStore'
import type { Printer } from '../../../types/api.types'
import { DEFAULT_PRINTER_PORT } from '../constants/adminConstants'
import { CardOverflowMenu } from './CardOverflowMenu'
import { EmptyState } from './EmptyState'
import { AdminBanner } from './AdminBanner'

interface PrinterFormState {
  name: string
  host: string
  port: number
}

const EMPTY_FORM: PrinterFormState = { name: '', host: '', port: DEFAULT_PRINTER_PORT }

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

// One printer per kitchen. Devices point at one of these; a device pointing at
// none never prints. The tab only shows for a restaurant property, so the
// property is whatever the top bar has selected.
export function PrintersTab(): JSX.Element {
  const propertyId = useSelectedPropertyId()

  const [printers, setPrinters] = useState<Printer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [editingPrinter, setEditingPrinter] = useState<Printer | null>(null)
  const [form, setForm] = useState<PrinterFormState>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 5000); return () => clearTimeout(t) } }, [success])
  useEffect(() => { if (error) { const t = setTimeout(() => setError(null), 5000); return () => clearTimeout(t) } }, [error])

  const loadPrinters = useCallback(async () => {
    if (propertyId == null) { setPrinters([]); setLoading(false); return }
    setLoading(true)
    try {
      setPrinters(await printersService.getAll(propertyId))
    } catch (e) {
      setError(errorMessage(e, 'Failed to load printers'))
    } finally {
      setLoading(false)
    }
  }, [propertyId])

  useEffect(() => { loadPrinters() }, [loadPrinters])

  function openCreateModal() {
    setEditingPrinter(null)
    setForm(EMPTY_FORM)
    setFormError(null)
    setShowModal(true)
  }

  function openEditModal(printer: Printer) {
    setEditingPrinter(printer)
    setForm({ name: printer.name, host: printer.host, port: printer.port ?? DEFAULT_PRINTER_PORT })
    setFormError(null)
    setShowModal(true)
  }

  async function handleSubmit() {
    const name = form.name.trim()
    const host = form.host.trim()
    // Only blank is checked here. What counts as a valid host is the API's call,
    // and it 400s with a message worth more than anything guessed client-side.
    if (!name) { setFormError('Give this printer a name'); return }
    if (!host) { setFormError('Printer address is required'); return }
    if (propertyId == null) { setFormError('Select a restaurant first'); return }

    setSubmitting(true)
    setFormError(null)
    try {
      if (editingPrinter) {
        await printersService.update(editingPrinter.id, { name, host, port: form.port })
        setSuccess('Printer updated')
      } else {
        await printersService.create({ property_id: propertyId, name, host, port: form.port })
        setSuccess('Printer added')
      }
      setShowModal(false)
      await loadPrinters()
    } catch (e) {
      // A 400 names what is wrong with the host — show the server's words, not ours.
      setFormError(errorMessage(e, 'Failed to save printer'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(printer: Printer) {
    if (!window.confirm(`Delete "${printer.name}"? Devices routed to it stop printing until they are reassigned.`)) return
    try {
      await printersService.delete(printer.id)
      setSuccess('Printer deleted')
      await loadPrinters()
    } catch (e) {
      setError(errorMessage(e, 'Failed to delete printer'))
    }
  }

  return (
    <div>
      <AdminBanner message={error} variant="error" />
      <AdminBanner message={success} variant="success" />

      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={openCreateModal}
          disabled={propertyId == null}
          className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          Add printer
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-brand-green" />
          <span className="ml-2 text-sm text-gray-500">Loading printers...</span>
        </div>
      ) : printers.length === 0 ? (
        <EmptyState icon={PrinterIcon} message="No printers yet" hint="Add one, then point each device at the kitchen it prints to" />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {printers.map(p => (
            <div key={p.id} className="min-w-0 rounded-lg border border-gray-200 bg-white p-5 transition-all duration-200 hover:border-brand-green/50 hover:shadow-md">
              <div className="mb-3 flex items-start justify-between gap-2">
                <h3 className="truncate text-[15px] font-semibold text-gray-900">{p.name}</h3>
                <CardOverflowMenu onEdit={() => openEditModal(p)} onDelete={() => handleDelete(p)} />
              </div>
              <div className="flex items-center gap-2 text-[13px]">
                <span className="w-16 shrink-0 text-gray-400">Address:</span>
                <span className="min-w-0 flex-1 truncate font-mono text-gray-700">
                  {p.host}:{p.port ?? DEFAULT_PRINTER_PORT}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="flex max-h-[90dvh] w-full max-w-md flex-col rounded-xl bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">{editingPrinter ? 'Edit printer' : 'Add printer'}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-600">{formError}</div>
              )}

              <div>
                <label htmlFor="printer-name" className="mb-1 block text-[13px] font-medium text-gray-700">Name*</label>
                <input
                  id="printer-name"
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="Ground floor kitchen"
                />
                <p className="mt-1 text-[12px] text-gray-400">What staff will pick from when assigning a device.</p>
              </div>

              <div>
                <label htmlFor="printer-host" className="mb-1 block text-[13px] font-medium text-gray-700">Printer address*</label>
                <input
                  id="printer-host"
                  type="text"
                  value={form.host}
                  onChange={e => setForm({ ...form, host: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="192.168.18.87"
                />
                <p className="mt-1 text-[12px] text-gray-400">
                  An IP address or hostname on the restaurant&rsquo;s network. No port, no http://
                </p>
              </div>

              <div>
                <label htmlFor="printer-port" className="mb-1 block text-[13px] font-medium text-gray-700">Printer port</label>
                <input
                  id="printer-port"
                  type="number"
                  value={form.port}
                  onChange={e => setForm({ ...form, port: parseInt(e.target.value, 10) || DEFAULT_PRINTER_PORT })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder={String(DEFAULT_PRINTER_PORT)}
                />
              </div>

              {/* Saving proves the address was stored, never that the printer answered —
                  the server is not on that network and never opens the socket. */}
              <p className="text-[12px] text-gray-400">
                Saving stores the address. It does not check that the printer is reachable.
              </p>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-3 sm:flex-row sm:gap-3 sm:px-6 sm:py-4">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-6 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
